/**
 * useCompoundEditor.js
 * ====================
 * Hook used on CompoundDetail to:
 *   - Track open time on a compound
 *   - Save field edits to Firestore (compound_overrides + compound_edits log)
 *   - Apply local optimistic updates so the UI reflects changes immediately
 *
 * Usage:
 *   const { editableCompound, saveField, sessionTimeSec, canEdit } = useCompoundEditor(compound);
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useWorkSession } from '../context/WorkSessionContext';
import { saveCompoundEdit, saveCompoundOverride } from '../firebase/firestoreService';

/** Get a nested value from an object by dot-path */
function getNestedValue(obj, path) {
  return path.split('.').reduce((cur, key) => cur?.[key], obj);
}

/** Set a nested value on an object by dot-path, returning a new object */
function setNestedValue(obj, path, value) {
  const clone = JSON.parse(JSON.stringify(obj ?? {}));
  const parts = path.split('.');
  let cur = clone;
  for (let i = 0; i < parts.length - 1; i++) {
    if (typeof cur[parts[i]] !== 'object' || cur[parts[i]] === null) {
      cur[parts[i]] = {};
    }
    cur = cur[parts[i]];
  }
  cur[parts[parts.length - 1]] = value;
  return clone;
}

export function useCompoundEditor(compound) {
  const { currentUser } = useAuth();
  const { userProfile, canEdit, sessionId, recordEdit } = useWorkSession();

  // Local state for optimistic UI updates — keyed by top-level field
  const [localOverrides, setLocalOverrides] = useState({});
  const [saving, setSaving]                 = useState(false);
  const [lastSaved, setLastSaved]           = useState(null);
  const [error, setError]                   = useState(null);

  // Track time spent on this compound
  const openedAtRef = useRef(Date.now());
  useEffect(() => {
    openedAtRef.current = Date.now();
    return () => { openedAtRef.current = null; };
  }, [compound?.id]);

  // Merge base compound with local optimistic overrides (deep merge at top level)
  const editableCompound = compound
    ? (() => {
        const merged = { ...compound };
        for (const [topKey, topVal] of Object.entries(localOverrides)) {
          if (typeof topVal === 'object' && topVal !== null && !Array.isArray(topVal) &&
              typeof merged[topKey] === 'object' && merged[topKey] !== null) {
            merged[topKey] = { ...merged[topKey], ...topVal };
          } else {
            merged[topKey] = topVal;
          }
        }
        return merged;
      })()
    : compound;

  /**
   * Save a single field edit.
   * @param {string} fieldPath — dot-path like "summary" or "pharmacology.halfLife"
   * @param {*}      newValue  — new value
   */
  const saveField = useCallback(async (fieldPath, newValue) => {
    if (!compound || !currentUser || !canEdit) return;

    const oldValue = getNestedValue(compound, fieldPath);
    if (JSON.stringify(oldValue) === JSON.stringify(newValue)) return; // no change

    setSaving(true);
    setError(null);

    const topKey = fieldPath.split('.')[0];

    try {
      // 1. Optimistic local update — update the top-level key using deep path set
      setLocalOverrides(prev => {
        const currentTop = prev[topKey] ?? compound[topKey] ?? {};
        if (fieldPath.includes('.')) {
          const subPath = fieldPath.slice(topKey.length + 1);
          const updatedTop = setNestedValue(
            typeof currentTop === 'object' ? currentTop : {},
            subPath,
            newValue
          );
          return { ...prev, [topKey]: updatedTop };
        } else {
          return { ...prev, [fieldPath]: newValue };
        }
      });

      const durationSec = Math.round((Date.now() - (openedAtRef.current ?? Date.now())) / 1000);
      const flatFields  = { [fieldPath]: { before: oldValue ?? null, after: newValue } };

      // 2. Write to compound_overrides using Firestore dot-notation for nested fields
      //    This allows { "pharmacology.halfLife": "4h" } to merge cleanly without overwriting siblings
      await saveCompoundOverride(
        compound.id,
        { [fieldPath]: newValue },   // firestoreService does setDoc({merge:true}) so dot keys work
        currentUser.uid
      );

      // 3. Write to compound_edits audit log
      await saveCompoundEdit({
        uid:          currentUser.uid,
        displayName:  userProfile?.displayName || currentUser.email,
        compoundId:   compound.id,
        compoundName: compound.name,
        fields:       flatFields,
        sessionId,
        durationSec,
      });

      // 4. Increment session edit count
      recordEdit(compound.id);

      setLastSaved(new Date());
    } catch (err) {
      console.error('[useCompoundEditor] saveField error:', err);
      setError(err.message || 'Save failed');
      // Roll back optimistic update
      setLocalOverrides(prev => {
        const next = { ...prev };
        delete next[topKey];
        return next;
      });
      throw err;
    } finally {
      setSaving(false);
    }
  }, [compound, currentUser, canEdit, sessionId, recordEdit, userProfile]);

  const sessionTimeSec = openedAtRef.current
    ? Math.round((Date.now() - openedAtRef.current) / 1000)
    : 0;

  return {
    editableCompound,
    saveField,
    saving,
    lastSaved,
    error,
    sessionTimeSec,
    canEdit,
  };
}
